<template>
  <main class="evalFigma w-full lastEvPg">
    <EvalShell title="Previous Evaluation" crumb="Previous Evaluation" :step="2" :job-id="$route.params.id" :unit-id="$route.query.unit_id">
      <!-- same look as Equipment Diagnosis (indoor-outdoor-equipment.vue): legend + counters, search, Outdoor/Indoor lists
           with the three status circles (read-only here: the last value). The checkbox picks the items to carry over. -->
      <div class="flex flex-wrap items-start justify-between gap-[15px] mb-[20px]">
        <ul class="list-none m-0 p-0 flex flex-col gap-[6px] text-[13px] leading-[19px] font-roboto text-[#5B616B]">
          <li v-for="item in radios" :key="item.value" class="flex items-center gap-[8px]">
            <span class="w-[16px] h-[16px] rounded-full border-[1.5px] bg-white flex-shrink-0" :style="{ borderColor: item.color }" />
            {{ item.legend }}
          </li>
        </ul>
        <div class="flex flex-wrap items-center gap-[10px]">
          <span v-for="item in radios.filter(r => r.value > 1)" :key="item.value"
            class="flex items-center gap-[8px] px-[8px] py-[3px] laptop:h-[41px] laptop:pl-[14px] laptop:pr-[16px] laptop:py-0 rounded-[5px] laptop:rounded-[8px] font-roboto"
            :style="{ backgroundColor: item.chipBg, color: item.chipText }">
            <span class="text-[14px] laptop:text-[16px] leading-[23px] font-bold">{{ options.filter(o => o.value == item.value).length }}</span>
            <span class="text-[12px] laptop:text-[13px] leading-[19px]">{{ item.label }}</span>
          </span>
          <span class="flex items-center gap-[8px] px-[8px] py-[3px] laptop:h-[41px] laptop:pl-[14px] laptop:pr-[16px] laptop:py-0 rounded-[5px] laptop:rounded-[8px] bg-[#EEF1FF] text-[#6C79E0] font-roboto">
            <span class="text-[14px] laptop:text-[16px] leading-[23px] font-bold">{{ selectedItems.length }}</span>
            <span class="text-[12px] laptop:text-[13px] leading-[19px]">Selected</span>
          </span>
        </div>
      </div>

      <div class="flex flex-wrap items-center justify-between gap-[10px] mb-[20px]">
        <label class="flex items-center gap-[10px] w-full tablet:w-[340px] h-[44px] m-0 px-[16px] rounded-[8px] border-1 border-[#DFE3EA] bg-white">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="11" cy="11" r="7" stroke="#A8AEB8" stroke-width="2" /><path d="M20 20l-3.5-3.5" stroke="#A8AEB8" stroke-width="2" stroke-linecap="round" /></svg>
          <input v-model="search" type="text" placeholder="Search a component…"
            class="flex-1 min-w-0 border-0 outline-none bg-transparent text-[14px] leading-[20px] font-roboto text-[#2E3138] placeholder:text-[#A8AEB8]">
        </label>
        <button type="button" class="h-[41px] px-[18px] rounded-[8px] border-1 text-[13px] font-medium font-roboto"
          :class="allSelected ? 'bg-[#6C79E0] border-[#6C79E0] text-white' : 'bg-white border-[#E3E6EC] text-[#5B616B]'"
          @click="selectedItems = allSelected ? [] : [...options]">
          {{ allSelected ? 'Clear selection' : 'Select all' }}
        </button>
      </div>

      <p v-if="previousEvaluation?.last_evaluation_date" class="m-0 mb-[12px] text-[13px] leading-[19px] font-roboto text-[#5B616B]">
        Last evaluated on {{ previousEvaluation.last_evaluation_date }}. Pick the items to carry over, or re-evaluate the whole system.
      </p>

      <!-- below laptop the two lists stack: chips jump between them, stuck under the header (like Equipment Diagnosis) -->
      <div v-if="sections.length > 1" ref="tabBar"
        class="laptop:hidden sticky z-[5] mb-3 -mx-[10px] px-[10px] tablet:-mx-[15px] tablet:px-[15px] py-[8px] bg-[#fff]" :style="{ top: headerHeight + 'px' }">
        <div class="flex gap-2">
          <button v-for="section in sections" :key="section.key" type="button"
            class="px-3 py-1 rounded-full text-[14px] font-roboto border-1"
            :class="selectedTab == section.key ? 'bg-[#7695FF] border-[#7695FF] text-white' : 'bg-white border-[#CCCCCC] text-[#333333]'"
            @click="tabClick(section.key)">
            {{ section.title }}
          </button>
        </div>
      </div>

      <div class="grid gap-[20px]" :class="sections.length > 1 ? 'desktop:grid-cols-2' : ''">
        <div v-for="section in sections" :id="'lastEv-' + section.key" :key="section.key" class="lastEvSection min-w-0 flex flex-col gap-[2px]">
          <h5 class="m-0 mb-[4px] min-h-[47px] flex items-center px-[20px] py-[12px] rounded-[8px] text-[16px] leading-[23px] font-roboto font-medium text-white bg-[#A3AAD2]">
            {{ section.title }}
          </h5>
          <label v-for="item in section.options" v-show="matches(item)" :key="item.id"
            class="flex items-center justify-between gap-[12px] min-h-[44px] m-0 px-[18px] py-[11px] rounded-[8px] border-1 cursor-pointer"
            :class="isSelected(item) ? 'bg-[#F4F5FF] border-[#6C79E0]' : (item.value == 3 ? 'bg-[#FEF7F7] border-[#F5C9C9]' : 'bg-[#FEFAF0] border-[#F6E3B8]')">
            <span class="flex items-center gap-[12px] min-w-0">
              <input type="checkbox" class="w-[16px] h-[16px] flex-shrink-0 accent-[#6C79E0] cursor-pointer" :checked="isSelected(item)"
                @change="toggleSelect(item, $event.target.checked)">
              <span class="text-[14px] leading-[20px] font-roboto text-[#2E3138]">
                {{ item.option }}
                <span v-if="item.booking_action" class="text-[12px] text-[#5B616B]"> · {{ item.booking_action }}</span>
              </span>
            </span>
            <span class="flex items-center gap-[6px] flex-shrink-0">
              <span v-for="radio in radios" :key="radio.value" :title="radio.label"
                class="w-[18px] h-[18px] rounded-full border-[1.5px] flex items-center justify-center"
                :style="{ borderColor: radio.color, backgroundColor: item.value == radio.value ? radio.color : '#FFFFFF' }">
                <svg v-if="item.value == radio.value" width="10" height="10" viewBox="0 0 14 14" aria-hidden="true">
                  <path d="M3 7.4l2.6 2.5L11 4.4" fill="none" stroke="#FFFFFF" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </span>
            </span>
          </label>
        </div>
      </div>
      <div class="flex flex-wrap justify-center items-center gap-[20px] laptop:gap-[10px] largeDesktop:gap-[20px] mt-[30px] laptop:static mobile:absolute -bottom-[20px] flex-col-reverse tablet:flex-row w-full left-0">
        <EvalButton variant="back" @click="backButtonClicked()" />
        <EvalButton @click="reEvaluateClicked()" arrowCircleClass="bg-[#fff] text-[#5E366A] laptop:bg-[#5E366A] laptop:text-[#fff] laptop:group-enabled:group-hover:bg-[#fff] laptop:group-enabled:group-hover:text-[#5E366A] " buttonClass="border-[#5E366A] !bg-[#5E366A] text-[#fff] laptop:!bg-[#fff] laptop:text-[#5E366A] laptop:enabled:hover:!bg-[#5E366A] laptop:enabled:hover:text-[#fff]">
          Re-Evaluate this system
        </EvalButton>
        <EvalButton :disabled="selectedItems?.length < 1" @click="continueClicked()" arrowCircleClass="group-disabled:opacity-[0.5] mobile:group-disabled:bg-[#fff] mobile:group-disabled:text-[#00B7C2] laptop:group-disabled:bg-[#00B7C2] laptop:group-disabled:text-[#fff]"
          buttonClass="tablet:disabled:!bg-[#CCF1F3] tablet:disabled:!border-[#CCF1F3] tablet:disabled:!text-[#FFFFFF] tablet:disabled:!opacity-[100] 
          laptop:disabled:!bg-[#fff] laptop:disabled:!border-[#00B7C2]/50 laptop:disabled:!text-[#00B7C2]/50">
          Continue using selected items
        </EvalButton>
      </div>
    </EvalShell>
  </main>
</template>

<script>
import { appHeaderBottom } from "@/helpers/appHeader";
import EvalShell from "@/components/nest_components/EvalShell.vue";
import EvalButton from "@/components/nest_components/EvalButton.vue";
import { mapActions, mapGetters } from "vuex";
import { useToast } from "vue-toastification";
import { evaluationApi } from "@/services/evaluationApi";
const toast = useToast();

export default {
  components: {
    EvalShell,
    EvalButton,
  },

  data() {
    return {
      jobId: this.$route.params.id,
      unit_id: this.$route.query.unit_id,
      options: [],
      selectedItems: [],
      isReEvaluate: false,
      search: "",
      // mobile unit chips
      selectedTab: "outdoor",
      headerHeight: 50,
      // same colours as Equipment Diagnosis; the previous evaluation only carries yellow and red items
      radios: [
        { value: 1, color: '#22C55E', chipBg: '#E7F7F0', chipText: '#1FA971', label: 'Operating', legend: "Operating as per manufacturer's specification" },
        { value: 2, color: '#F5B301', chipBg: '#FEF6E6', chipText: '#C98305', label: 'Needs attention', legend: "Not operating as per manufacturer's specification" },
        { value: 3, color: '#EF4444', chipBg: '#FDECEC', chipText: '#C43333', label: 'Critical', legend: 'Non Operation / Critical' },
      ],
    };
  },


  computed: {
    ...mapGetters([
      "previousEvaluation",
      "bookingResponse",
      "previousEvaluationResponse",
      "storeChecksResponse",
      "getDiagnosisOptions"
    ]),
    // grouped like the Equipment Diagnosis lists (option type: indoor / outdoor, strip_indoor / strip_outdoor …)
    sections() {
      const isIndoor = o => String(o.type).toLowerCase().includes('indoor');
      const outdoor = this.options.filter(o => !isIndoor(o));
      const indoor = this.options.filter(isIndoor);
      return [
        { key: 'outdoor', title: 'Outdoor Unit', options: outdoor },
        { key: 'indoor', title: 'Indoor Unit', options: indoor },
      ].filter(sec => sec.options.length);
    },
    allSelected() {
      return this.options.length > 0 && this.options.every(this.isSelected);
    },
  },


  watch: {
    bookingResponse: function () {
      if (this.bookingResponse !== null) {
        this.getDiagnosisOption({
          booking_id: this.jobId,
          diagnosis_type: "last_evaluation",
        });
      }
    },

    previousEvaluation: function () {
      this.options = this.previousEvaluation.options || []
      // the restored last_evaluation draft is per booking, not per unit: keep only this unit's items
      // (a draft from another unit showed "2 Selected" with nothing ticked)
      this.selectedItems = this.options.filter(o => this.selectedItems.some(s => s.id === o.id))
    },

    getDiagnosisOptions: function () {
      if (!this.$helper.isEmpty(this.getDiagnosisOptions)){
        this.selectedItems = JSON.parse(this.getDiagnosisOptions[0]?.diagnosis_data)
      }
      this.getPreviousEvaluation({
        booking_id: this.jobId,
        unit_id: this.unit_id,
      });
    },

    storeChecksResponse: async function () {
      let options = this.isReEvaluate ? [] : this.selectedItems;
      // the previous booking's options can have other ids than this unit's current list (e.g. the unit's type changed:
      // "Compressor" 356 vs 402); re-evaluate/store and revamp/options match by id only, so point those to the
      // current option with the same name, else the carried value is lost (row stays green)
      if (options.length) {
        const list = await evaluationApi.equipmentList(this.jobId, this.unit_id);
        if (list.ok) {
          const current = [...(list.body?.indoor?.body || []), ...(list.body?.outdoor?.body || [])];
          options = options.map((o) => {
            if (current.some(c => Number(c.diagnostic_option_id) === Number(o.id))) return o;
            const same = current.find(c => c.option === o.option);
            return same ? { ...o, id: same.diagnostic_option_id } : o;
          });
        }
      }
      this.postPreviousEvaluation({
        booking_id: this.jobId,
        options: JSON.stringify(options)
      });
    },

    previousEvaluationResponse: async function () {
      if (this.previousEvaluationResponse) {
        // re-evaluate/store only overwrites the picked items in the booking's equipment_checks draft, so values from an
        // earlier attempt (another unit, other items) stayed and showed up as extra yellow/red rows. Start the draft
        // again from this unit's list, which already has the carried-over values.
        const list = await evaluationApi.equipmentList(this.jobId, this.unit_id);
        if (list.ok) {
          await evaluationApi.saveDraft(this.jobId, "equipment_checks", {
            indoor: list.body?.indoor?.body ?? [],
            outdoor: list.body?.outdoor?.body ?? [],
          });
        }
        this.$router.push({
          path: `/equipment/${this.$route.params.id}`,
          query: {
            unit_id: this.unit_id,
            is_from_mobile: this.$route.query.is_from_mobile,
          },
        });
      }
    },
  },

  mounted() {
    this.getBookingDetails({
      booking_id: this.jobId,
    });
    window.addEventListener("scroll", this.updateOffset);
    window.addEventListener("scroll", this.onSectionScroll);
    this.headerHeight = appHeaderBottom(this.headerHeight);
  },

  beforeUnmount() {
    window.removeEventListener("scroll", this.onSectionScroll);
  },

  methods: {
    tabClick(key) {
      this.selectedTab = key;
      const element = document.getElementById("lastEv-" + key);
      if (!element) return;
      // land the section right under the header + the sticky chip bar
      const barHeight = this.$refs.tabBar?.offsetHeight || 0;
      window.scrollTo({
        top: element.getBoundingClientRect().top + window.pageYOffset - this.headerHeight - barHeight - 8,
        behavior: "smooth",
      });
    },
    // keep the header offset current and highlight the section in the middle of the screen
    onSectionScroll() {
      this.headerHeight = appHeaderBottom(this.headerHeight);
      document.querySelectorAll(".lastEvSection").forEach((section) => {
        const rect = section.getBoundingClientRect();
        if (rect.top <= window.innerHeight / 2 && rect.bottom >= window.innerHeight / 2) {
          this.selectedTab = section.id.replace("lastEv-", "");
        }
      });
    },

    ...mapActions([
      "getPreviousEvaluation",
      "getBookingDetails",
      "postPreviousEvaluation",
      "getDiagnosisOption",
      "storeChecks"
    ]),

    updateOffset() {
    },

    backButtonClicked() {
      this.$router.go(-1);
    },

    reEvaluateClicked() {
      this.isReEvaluate = true
      this.storeChecks({
        booking_id: this.jobId,
        diagnosis_type: "last_evaluation",
        diagnosis_data: JSON.stringify([]),
      });
    },

    continueClicked() {
      if (this.selectedItems?.length > 0) {
        this.isReEvaluate = false
        this.storeChecks({
          booking_id: this.jobId,
          diagnosis_type: "last_evaluation",
          diagnosis_data: JSON.stringify(this.selectedItems),
        });
      } else {
        toast.error("Please select at least one item.");
      }
    },

    isSelected(item) {
      return this.selectedItems.some(i => i.id === item.id);
    },
    matches(item) {
      return !this.search.trim() || String(item.option).toLowerCase().includes(this.search.trim().toLowerCase());
    },

    toggleSelect(item, val) {
      if (val) {
        this.selectedItems.push(item);
      } else {
        this.selectedItems = this.selectedItems.filter(i => i.id !== item.id);
      }
    }
  },
};
</script>

<style>
.heading br {
  display: none;
}

.heading a img {
  width: 40px;
}

.highlight {
  border: 2px solid red;
  padding: 10px;
}

.minHeight {
  min-height: 0px;
}


@media screen and (max-width: 575px) {
  .heading br {
    display: block;
  }
}
</style>
